import type { CustomFieldValues } from '@taki/data-model';

/**
 * Fondation de donnees de l'app Cat Logger Outside (suivi des chats en sortie).
 *
 * Regles de la cage :
 *  - acces donnees UNIQUEMENT via `@taki/sdk` (aucun import firebase/firestore) ;
 *  - les chats sont des `asset` de type 'cat' (MEME vocabulaire que l'app
 *    cat-manager-six : les deux apps parlent des memes fiches) ;
 *  - une sortie est une `custody` de type 'outside-visit' : la famille socle
 *    "prise en charge temporaire" porte nativement le cycle remise -> echeance
 *    -> restitution, qui est exactement celui d'une sortie de chat :
 *      receivedAt = date/heure de SORTIE (le chat quitte le refuge),
 *      dueBackAt  = retour PREVU,
 *      returnedAt = retour EFFECTIF (vide = chat encore dehors).
 *  - un SEUL type local par famille (`cat`, `outside-visit`), declare dans
 *    `taki.app.json`. Le motif de la sortie n'est donc PAS un `type` custody :
 *    il vit dans `customFields.reason`, ce qui permet d'en ajouter sans
 *    modifier la fiche de l'app.
 *
 * Ce fichier est le CONTRAT partage par l'ecran (memes noms d'exports et memes
 * cles customFields).
 */

// --- Types locaux declares dans taki.app.json ------------------------------

/** Type local de la famille `asset` : une fiche chat. */
export const CAT_TYPE = 'cat';

/** Type local de la famille `custody` : une sortie de chat. */
export const OUTING_TYPE = 'outside-visit';

// --- Motifs de sortie ------------------------------------------------------

export interface ReasonOption {
  value: string;
  label: string;
}

/**
 * Motifs livres avec l'app (base prompt du fondateur). La liste affichee y
 * ajoute les motifs deja utilises par des sorties existantes : un motif ajoute
 * dans les Parametres devient donc permanent des qu'une sortie l'emploie.
 */
export const DEFAULT_REASONS: ReasonOption[] = [
  { value: 'vet', label: 'Veterinaire' },
  { value: 'adoption-trial', label: 'Test avant adoption' },
  { value: '7wwm', label: 'Programme 7WWM' },
];

/** Cle machine stable a partir d'un libelle saisi (ex. "Toilettage" -> "toilettage"). */
export function reasonValueOf(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // retire les accents
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Libelle affichable d'un motif : l'option connue, sinon la valeur brute. */
export function reasonLabelOf(options: ReasonOption[], value: string): string {
  return options.find((r) => r.value === value)?.label ?? value;
}

// --- Valeurs par defaut d'une fiche chat -----------------------------------

/**
 * Sac `customFields` d'un chat cree depuis cette app.
 *
 * Duplique volontairement la forme utilisee par cat-manager-six : les deux apps
 * partagent les memes fiches `asset`/'cat', mais AUCUNE app n'importe le code
 * d'une autre (zero dependance app -> app, cf. docs/PLAYBOOK-APP.md section 4). Un chat
 * cree ici s'ouvre donc normalement dans cat-manager-six.
 */
export function newCatCustomFields(): CustomFieldValues {
  return {
    character: [],
    adoptionStatus: 'resident',
    photoUrl: '',
    birthYear: null,
    diet: '',
    vaccinesUpToDate: true,
  };
}

// --- Types vue (derives des familles socle) --------------------------------

export interface Cat {
  id: string;
  name: string;
  photoUrl: string;
}

export interface Outing {
  id: string;
  catId: string;
  reason: string;
  personName: string;
  personPhone: string;
  leftAt: Date | null; // champ natif receivedAt
  dueBackAt: Date | null; // champ natif dueBackAt
  returnedAt: Date | null; // champ natif returnedAt ; null = encore dehors
}

// --- Helpers internes de lecture defensive ---------------------------------

/** Recupere le sac customFields d'un document quel qu'il soit, sans planter. */
function customFieldsOf(source: unknown): Record<string, unknown> {
  if (source && typeof source === 'object' && 'customFields' in source) {
    const cf = (source as { customFields?: unknown }).customFields;
    if (cf && typeof cf === 'object') {
      return cf as Record<string, unknown>;
    }
  }
  return {};
}

/** Lit une propriete de premier niveau d'un objet inconnu. */
function fieldOf(source: unknown, key: string): unknown {
  if (source && typeof source === 'object' && key in source) {
    return (source as Record<string, unknown>)[key];
  }
  return undefined;
}

/** Convertit une valeur Timestamp | Date | string | inconnu en Date ou null. */
function toDate(value: unknown): Date | null {
  if (value == null) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'object' && typeof (value as { toDate?: unknown }).toDate === 'function') {
    try {
      const d = (value as { toDate: () => Date }).toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/** Lit une chaine de premier niveau, chaine vide par defaut. */
function stringOf(source: unknown, key: string): string {
  const raw = fieldOf(source, key);
  return typeof raw === 'string' ? raw : '';
}

// --- Helpers de mapping publics --------------------------------------------

/** Lit un asset (type 'cat') vers la vue Cat. */
export function assetToCat(asset: unknown): Cat {
  const cf = customFieldsOf(asset);
  return {
    id: stringOf(asset, 'id'),
    name: stringOf(asset, 'name'),
    photoUrl: typeof cf.photoUrl === 'string' ? cf.photoUrl : '',
  };
}

/**
 * Lit une custody (type 'outside-visit') vers la vue Outing.
 * Les dates viennent des champs natifs de la famille ; le SDK les rend en
 * `Date` (cf. `createConverter`), le mapping reste defensif par principe.
 */
export function custodyToOuting(custody: unknown): Outing {
  const cf = customFieldsOf(custody);
  return {
    id: stringOf(custody, 'id'),
    catId: stringOf(custody, 'itemAssetId'),
    reason: typeof cf.reason === 'string' ? cf.reason : '',
    personName: typeof cf.personName === 'string' ? cf.personName : '',
    personPhone: typeof cf.personPhone === 'string' ? cf.personPhone : '',
    leftAt: toDate(fieldOf(custody, 'receivedAt')),
    dueBackAt: toDate(fieldOf(custody, 'dueBackAt')),
    returnedAt: toDate(fieldOf(custody, 'returnedAt')),
  };
}

/** Une sortie est en cours tant que le retour effectif n'est pas renseigne. */
export function isOngoing(outing: Outing): boolean {
  return outing.returnedAt == null;
}

/** Le document est-il actif (non archive) ? Le soft delete se fait via archivedAt. */
export function isActive(doc: unknown): boolean {
  return toDate(fieldOf(doc, 'archivedAt')) == null;
}

/** Le document appartient-il au type local attendu ? */
export function isType(doc: unknown, type: string): boolean {
  return fieldOf(doc, 'type') === type;
}

// --- Helpers de date pour les formulaires ----------------------------------

/** Date du jour au format yyyy-mm-dd (valeur par defaut des champs date). */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Heure courante au format hh:mm (valeur par defaut des champs heure). */
export function nowTime(): string {
  return new Date().toTimeString().slice(0, 5);
}

/**
 * Combine un champ date (yyyy-mm-dd) et un champ heure (hh:mm) en Date locale.
 * Retourne null si la date est absente ou invalide (l'heure seule ne suffit pas).
 */
export function toDateTime(dateIso: string, time: string): Date | null {
  if (dateIso.trim() === '') {
    return null;
  }
  const d = new Date(`${dateIso}T${time.trim() === '' ? '00:00' : time}`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Formate une Date en "jj/mm/aaaa a hh:mm" ; tiret si absente. */
export function formatDateTime(value: Date | null): string {
  if (value == null) {
    return '-';
  }
  const date = value.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const time = value.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return `${date} a ${time}`;
}

/** Une sortie est en retard si le retour prevu est depasse et le chat pas rentre. */
export function isOverdue(outing: Outing, now: Date): boolean {
  return isOngoing(outing) && outing.dueBackAt != null && outing.dueBackAt.getTime() < now.getTime();
}
